// Arc Flash Labels: KE's study in, one label row per enclosure out. Pure, unit tested.
// Words: AF_LABEL_IMPORT_WORDS.md (session I8). Mockup: MOCKUP_AF_LABELS.html, approved by Robert 2026-10-21.
// Reference script: claude/baconton_label_rows.py. Test case: the completed Baconton study (site 10, KE 2201047).
//
// The sheets come in as SKM writes them: four header rows, one reading per row, a footer of PPE levels.
// Worst reading wins per enclosure. Isolate At is the winning reading's clearing device, named as an Asset ID.
// Nothing here writes anything. The page shows the rows; the server publishes them.

import { restrictedApproachOf } from "./eewp";
import { f1 } from "./py-format";

// ---- What comes in ----

/** One sheet as the reader returns it: rows of cells, header rows included. */
export type SheetRows = readonly (readonly unknown[])[];

/** One SKM arc flash reading: one bus, one clearing device. */
export interface Reading {
  /** SKM Protective Device Name. Can be an SKM limit case such as "MaxTripTime @2.0s". */
  device: string;
  /** The bus, with any "(device LineSide)" note taken off. */
  bus: string;
  /** True for the Incoming reading (SKM's LineSide note). */
  incoming: boolean;
  /** The device named in the LineSide note. */
  incomingDevice: string;
  /** The User Notes column (the full Asset ID) on the Model Tag path. Blank on the legacy path. */
  userNotes: string;
  volts: number;
  busBoltedKa: number;
  busArcingKa: number;
  /** Fault current through the clearing device. */
  deviceBoltedKa: number;
  deviceArcingKa: number;
  tripSeconds: number;
  openingSeconds: number;
  equipmentType: string;
  electrode: string;
  boxIn: string;
  gapMm: number | null;
  /** Arc flash boundary in inches. Null on an OVERDUTY reading. */
  afbIn: number | null;
  workingDistanceIn: number | null;
  /** Incident energy, cal/cm². Null on an OVERDUTY reading. */
  ieCal: number | null;
  overduty: boolean;
  /** SKM's notes column, as written: "Level 1 (*N3)  (*S1)". */
  notes: string;
}

/** One device from the Data Visualizer settings export. */
export interface DeviceSettings {
  name: string;
  type: string;
  interruptingKa: number | null;
  /** Field name to value, per scenario, as SKM wrote it. */
  scenarios: Record<string, Record<string, string>>;
  /** Fields where the COORDINATION scenario differs from the first scenario: recommended changes. */
  changes: { field: string; asFound: string; recommended: string }[];
}

/** The registry fields the import reads. sites/{site}/assets/{assetId}. */
export interface RegistryAsset {
  assetId: string;
  equipmentType?: string;
  nominalVolts?: number | null;
  /** Text as written: "3 ft 6 in." */
  limitedApproach?: string;
  /** "Fixed Conductor" or "Moveable Conductor". */
  conductorType?: string;
}

// ---- Sheet readers ----

const cell = (v: unknown): string => (v === null || v === undefined ? "" : String(v)).trim();
const num = (v: unknown): number | null => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = parseFloat(cell(v));
  return Number.isFinite(n) ? n : null;
};

/** SKM length text to inches: 4' 3" is 51, 7" is 7. OVERDUTY and blanks are null. */
export function inchesOf(v: unknown): number | null {
  const s = cell(v);
  const m = /^(?:(\d+)'\s*)?(\d+)"$/.exec(s);
  if (!m) return null;
  return Number(m[1] ?? 0) * 12 + Number(m[2]);
}

/** "EA1-PNL-01 (EA1-PNL-01-MB LineSide)" to the bus and the LineSide device. Other parentheses stay in the name. */
export function splitBusName(raw: string): { bus: string; incoming: boolean; incomingDevice: string } {
  const m = /^(.*) \(([^()]+) LineSide\)$/.exec(raw.trim());
  return m ? { bus: m[1]!.trim(), incoming: true, incomingDevice: m[2]!.trim() } : { bus: raw.trim(), incoming: false, incomingDevice: "" };
}

/** The column words SKM stacks over four header rows, joined: "Bus Bolted Fault (kA)". */
function headerWords(rows: SheetRows, depth: number): string[] {
  const width = Math.max(0, ...rows.slice(0, depth).map((r) => r.length));
  return Array.from({ length: width }, (_, c) =>
    rows
      .slice(0, depth)
      .map((r) => cell(r[c]))
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " "),
  );
}

/** SKM column headers, as the four header rows join up. KE's report layouts differ, so columns are found by name. */
const COLUMNS = {
  device: /^Protective Device Name/i,
  bus: /^Bus Name/i,
  user: /^User Notes/i,
  kv: /^Bus kV/i,
  busBolted: /^Bus Bolted Fault/i,
  busArcing: /^Bus Arcing Fault/i,
  devBolted: /^Prot Dev Bolted Fault/i,
  devArcing: /^Prot Dev Arcing Fault/i,
  trip: /^Trip\/ ?Delay Time/i,
  opening: /^Breaker Opening/i,
  equipType: /^Equip Type/i,
  electrode: /^Electrode Config/i,
  boxH: /^Box Height/i,
  boxW: /^Box Width/i,
  boxD: /^Box Depth/i,
  gap: /^Gap/i,
  afb: /^Arc Flash Boundary/i,
  wd: /^Working Distance/i,
  ie: /^Incident Energy/i,
  notes: /^PPE Level/i,
} as const;
const REQUIRED: (keyof typeof COLUMNS)[] = ["device", "bus", "kv", "busBolted", "afb", "wd", "ie"];

/** Where each field sits, by header name. -1 for a column the layout does not have. */
function columnsOf(rows: SheetRows): Record<keyof typeof COLUMNS, number> {
  const h = headerWords(rows, 4);
  const at = Object.fromEntries(Object.entries(COLUMNS).map(([k, re]) => [k, h.findIndex((w) => re.test(w))])) as Record<keyof typeof COLUMNS, number>;
  const missing = REQUIRED.filter((k) => at[k] < 0);
  if (missing.length) throw new Error(`This is not an SKM arc flash sheet. Missing columns: ${missing.join(", ")}.`);
  return at;
}

/** Every reading in an SKM IEEE 1584 sheet (Detail or Summary). The header and footer rows are skipped. */
export function readArcFlashSheet(rows: SheetRows): Reading[] {
  const c = columnsOf(rows);
  const out: Reading[] = [];
  for (const r of rows.slice(4)) {
    const device = cell(r[c.device]);
    const rawBus = cell(r[c.bus]);
    const at = (i: number) => (i >= 0 ? r[i] : undefined);
    const kv = num(r[c.kv]);
    if (!device || !rawBus || kv === null) continue; // blank rows between buses and the PPE level footer
    const b = splitBusName(rawBus);
    const ieText = cell(r[c.ie]);
    const overduty = /^OVERDUTY$/i.test(ieText);
    out.push({
      device,
      bus: b.bus,
      incoming: b.incoming,
      incomingDevice: b.incomingDevice,
      userNotes: cell(at(c.user)),
      volts: Math.round(kv * 1000),
      busBoltedKa: num(r[c.busBolted]) ?? 0,
      busArcingKa: num(at(c.busArcing)) ?? 0,
      deviceBoltedKa: num(at(c.devBolted)) ?? 0,
      deviceArcingKa: num(at(c.devArcing)) ?? 0,
      tripSeconds: num(at(c.trip)) ?? 0,
      openingSeconds: num(at(c.opening)) ?? 0,
      equipmentType: cell(at(c.equipType)),
      electrode: cell(at(c.electrode)),
      boxIn: [at(c.boxH), at(c.boxW), at(c.boxD)].map(cell).join(" x "),
      gapMm: num(at(c.gap)),
      afbIn: inchesOf(r[c.afb]),
      workingDistanceIn: inchesOf(r[c.wd]),
      ieCal: overduty ? null : num(ieText),
      overduty,
      notes: cell(at(c.notes)).replace(/\s+/g, " "),
    });
  }
  return out;
}

/** The Data Visualizer export: Component, Field, then one column per scenario. */
export function readDeviceSettings(rows: SheetRows): Map<string, DeviceSettings> {
  const head = (rows[0] ?? []).map(cell);
  const scen = head.slice(2).filter(Boolean);
  const out = new Map<string, DeviceSettings>();
  let cur: DeviceSettings | null = null;
  for (const r of rows.slice(1)) {
    const name = cell(r[0]);
    if (name) {
      cur = { name, type: "", interruptingKa: null, scenarios: Object.fromEntries(scen.map((s) => [s, {}])), changes: [] };
      out.set(name, cur);
    }
    if (!cur) continue;
    const field = cell(r[1]);
    if (!field) continue;
    scen.forEach((s, i) => {
      cur!.scenarios[s]![field] = cell(r[2 + i]);
    });
    const first = cell(r[2]);
    if (field === "Type") cur.type = first;
    if (/^InterruptingRating/i.test(field)) cur.interruptingKa = num(first);
    const coord = scen.indexOf("COORDINATION");
    if (coord > 0) {
      const rec = cell(r[2 + coord]);
      if (rec !== first) cur.changes.push({ field, asFound: first, recommended: rec });
    }
  }
  return out;
}

// ---- Matching to the registry (AF_KE_IMPORT_NOTES.md, legacy path) ----

export type MatchState = "Matched" | "Suggested" | "Needs ID";

export interface Match {
  state: MatchState;
  /** The full Asset ID. Blank on Needs ID. */
  assetId: string;
  note: string;
}

/** A bucket or space inside a housing: A1, B12, E2a. */
const POSITION = /^[A-Z]{1,2}\d{1,2}[ab]?$/;
/** Part codes inside an asset take a dot (IEA_GEAR_VIEW_WORDS section 8). SKM's FU is our FUS. */
export const PART_CODES: Record<string, string> = { FU: "FUS", FUS: "FUS", MB: "MB", CB: "CB", MCP: "MCP", SW: "SW" };
/** SKM's limit cases. Not devices; nothing to isolate at. */
export const isLimitCase = (device: string) => /^(MaxTripTime|IndMtr Decay)/i.test(device);

/** Edit distance, for close matches. */
function distance(a: string, b: string): number {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)] as number[]);
  for (let j = 1; j <= b.length; j++) d[0]![j] = j;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++)
      d[i]![j] = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
  return d[a.length]![b.length]!;
}

export class StudyRegistry {
  readonly site: string;
  readonly byId: Map<string, RegistryAsset>;
  /** IDs without the site number: "ER1-MCC-01". */
  readonly short: Set<string>;

  constructor(site: string, assets: readonly RegistryAsset[]) {
    this.site = site;
    this.byId = new Map(assets.map((a) => [a.assetId, a]));
    const pre = `${site}-`;
    this.short = new Set(assets.map((a) => a.assetId).filter((id) => id.startsWith(pre)).map((id) => id.slice(pre.length)));
  }

  full(shortId: string) {
    return `${this.site}-${shortId}`;
  }

  has(shortId: string) {
    return this.short.has(shortId);
  }

  isUtility(shortId: string) {
    return /utility/i.test(this.byId.get(this.full(shortId))?.equipmentType ?? "");
  }

  /** Buckets inside a housing: registry children named by position. They take the housing's values at Publish. */
  bucketsOf(shortId: string): string[] {
    const pre = `${shortId}-`;
    return [...this.short]
      .filter((s) => s.startsWith(pre) && POSITION.test(s.slice(pre.length)))
      .sort((a, b) => a.localeCompare(b, "en", { numeric: true }))
      .map((s) => this.full(s));
  }

  /** The closest registry IDs in the same room, best first. */
  near(name: string, n = 4): string[] {
    const room = name.split("-")[0] ?? "";
    return [...this.short]
      .filter((s) => s.split("-")[0] === room)
      .map((s) => [s, distance(name, s)] as const)
      .filter(([, d]) => d <= Math.max(3, Math.floor(name.length / 3)))
      .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
      .slice(0, n)
      .map(([s]) => this.full(s));
  }

  /** One close match with no rival: distance 1 or 2, and nothing else as close. */
  private oneClose(name: string): string | null {
    const scored = [...this.short].map((s) => [s, distance(name, s)] as const).filter(([, d]) => d <= 2);
    if (!scored.length) return null;
    const best = Math.min(...scored.map(([, d]) => d));
    const top = scored.filter(([, d]) => d === best);
    return top.length === 1 ? top[0]![0] : null;
  }

  /** A bus name to an Asset ID. */
  matchBus(bus: string): Match {
    if (this.has(bus)) return { state: "Matched", assetId: this.full(bus), note: "" };
    const close = this.oneClose(bus);
    if (close) return { state: "Suggested", assetId: this.full(close), note: `Close match: ${bus} to ${close}` };
    return { state: "Needs ID", assetId: "", note: "No Asset ID matches this SKM name" };
  }

  /** A clearing device to its Asset ID: a bucket keeps its dash, a part takes a dot. */
  matchDevice(device: string): Match {
    if (!device || isLimitCase(device)) return { state: "Needs ID", assetId: "", note: "SKM limit case, not a device" };
    const prefixes = [...this.short].filter((s) => device === s || device.startsWith(`${s}-`));
    let base = prefixes.sort((a, b) => b.length - a.length)[0];
    let state: MatchState = "Matched";
    let note = "";
    if (!base) {
      // Try the front of the name against a close registry ID: EA-MSP-01-MB to EA5-MSP-01.
      const parts = device.split("-");
      for (let k = parts.length; k >= 2 && !base; k--) {
        const close = this.oneClose(parts.slice(0, k).join("-"));
        if (close) {
          base = close;
          state = "Suggested";
          note = `Close match: ${parts.slice(0, k).join("-")} to ${close}`;
          device = [close, ...parts.slice(k)].join("-");
        }
      }
      if (!base) return { state: "Needs ID", assetId: "", note: "No Asset ID starts this name" };
    }
    const rest = device.slice(base.length).replace(/^-/, "");
    if (!rest) return { state, assetId: this.full(base), note };
    if (this.has(`${base}-${rest}`)) return { state, assetId: this.full(`${base}-${rest}`), note: note || "A bucket keeps its dash" };
    const code = PART_CODES[rest];
    if (code) return { state, assetId: `${this.full(base)}.${code}`, note: note || `${rest} is a part inside ${base}, so a dot` };
    return { state: "Suggested", assetId: this.full(base), note: `Panel breaker ${rest}: SKM gives no circuit number. Set the circuit if you know it.` };
  }
}

// ---- Boundaries (section 5) ----

/** "3 ft 6 in." to 42. A bare number is inches. Words such as "Avoid Contact" are null. */
export function boundaryInches(text: string | undefined): number | null {
  const s = (text ?? "").trim();
  const m = /^(\d+)\s*ft\s*(\d+)\s*in\.?$/i.exec(s) ?? /^(\d+)\s*'\s*(\d+)\s*"?$/.exec(s);
  if (m) return Number(m[1]) * 12 + Number(m[2]);
  const n = /^(\d+(?:\.\d+)?)\s*(?:in\.?)?$/i.exec(s);
  return n ? Number(n[1]) : null;
}

/** NFPA 70E Table 130.4(E)(a), limited approach, in inches. Moveable conductors read the wider column. */
export function limitedApproachIn(volts: number, moveable = false): number | null {
  if (volts < 50) return null;
  if (volts <= 750) return moveable ? 120 : 42;
  if (volts <= 15000) return moveable ? 120 : 60;
  if (volts <= 36000) return moveable ? 120 : 72;
  return null;
}

/** The JSP's restricted approach rule, in inches. Null for "Avoid contact" and below 50 V. */
export function restrictedApproachIn(volts: number): number | null {
  return boundaryInches(restrictedApproachOf(volts));
}

// ---- One label row per enclosure (sections 4 to 6) ----

export type LabelKind = "STANDARD" | "ABOVE CEILING" | "OVERDUTY";
export type StatusWord = "Needs ID" | "Needs Isolate At" | "Needs Interrupting Rating" | "Check LAB" | "Check Voltage";

export interface LabelRow {
  /** SKM's bus name, as the study wrote it. */
  bus: string;
  match: Match;
  kind: LabelKind;
  stock: "WARNING" | "DANGER";
  volts: number;
  labIn: number | null;
  rabIn: number | null;
  /** The 70E limited approach for this voltage, the cross-check. */
  lab70eIn: number | null;
  /** Null on overduty. Kept as SKM gave it; the label prints one decimal. */
  ieCal: number | null;
  workingDistanceIn: number | null;
  afbIn: number | null;
  faultKa: number;
  interruptingKa: number | null;
  /** The device whose rating failed, from the device settings. Blank when SKM failed the bus equipment. */
  failedDevice: string;
  /** Why the rating is what it is, or why it is missing. */
  ratingNote: string;
  isolateAt: Match;
  /** The winning reading. */
  winner: Reading;
  /** Every reading for this enclosure, back office only. */
  readings: Reading[];
  /** Registry buckets that take this row's values at Publish. */
  buckets: string[];
  status: StatusWord[];
  /** Printed with a warning: af_label.py's flags. */
  warnings: string[];
  /** The Summary's worst value as printed, or null with no Summary. */
  summaryValue: number | "OVERDUTY" | null;
  summaryMatches: boolean | null;
  /** The device the Summary names for this bus, when it differs from ours on the same energy. */
  summaryDevice: string;
}

export interface StudyImport {
  site: string;
  path: "Model Tag" | "Legacy";
  rows: LabelRow[];
  /** Utility buses: no row, kept in the study record. */
  utility: { bus: string; readings: Reading[] }[];
  readingCount: number;
  busCount: number;
  summaryCount: number;
  deviceCount: number;
  /** Recommended setting changes from the COORDINATION scenario. Back office. */
  settingChanges: { device: string; field: string; asFound: string; recommended: string }[];
}

/** One decimal, exactly as the label prints it (af_label.py's f"{x:.1f}"). */
const oneDecimal = (n: number) => Number(f1(n));

/**
 * Worst reading wins. OVERDUTY beats any number. Then the highest IE as the label prints it (one decimal).
 * On a tie: the Incoming reading (its device is upstream and kills the line side too), then a real device
 * over SKM's limit case, then the device carrying the most fault current.
 * Proposed in MOCKUP_AF_LABELS.html, approved by Robert 2026-10-21.
 */
export function worstOf(readings: readonly Reading[]): Reading {
  const rank = (r: Reading) => [r.overduty ? 1 : 0, r.overduty ? 0 : oneDecimal(r.ieCal ?? 0), r.incoming ? 1 : 0, isLimitCase(r.device) ? 0 : 1, r.deviceBoltedKa];
  return [...readings].sort((a, b) => {
    const x = rank(a), y = rank(b);
    for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return x[i]! - y[i]!;
    return 0;
  })[readings.length - 1]!;
}

/** Ready, or the words that say what the row needs. Needs words block; Check words do not. */
export function statusOf(r: Pick<LabelRow, "match" | "isolateAt" | "kind" | "interruptingKa" | "labIn" | "lab70eIn" | "volts">, recordVolts?: number | null): StatusWord[] {
  const s: StatusWord[] = [];
  if (r.match.state !== "Matched") s.push("Needs ID");
  if (r.isolateAt.state !== "Matched") s.push("Needs Isolate At");
  if (r.kind === "OVERDUTY" && r.interruptingKa === null) s.push("Needs Interrupting Rating");
  if (r.labIn === null || (r.lab70eIn !== null && r.labIn !== r.lab70eIn)) s.push("Check LAB");
  if (typeof recordVolts === "number" && recordVolts > 0 && recordVolts !== r.volts) s.push("Check Voltage");
  return s;
}
export const isBlocking = (s: readonly StatusWord[]) => s.some((w) => w.startsWith("Needs"));

/** The whole import. Detail is required; Summary and device settings are optional. */
export function buildStudy(input: {
  site: string;
  registry: readonly RegistryAsset[];
  detail: SheetRows;
  summary?: SheetRows;
  settings?: SheetRows;
}): StudyImport {
  const reg = new StudyRegistry(input.site, input.registry);
  const readings = readArcFlashSheet(input.detail);
  const summary = input.summary ? readArcFlashSheet(input.summary) : [];
  const devices = input.settings ? readDeviceSettings(input.settings) : new Map<string, DeviceSettings>();
  const modelTag = readings.some((r) => r.userNotes);

  const byBus = new Map<string, Reading[]>();
  for (const r of readings) {
    if (!byBus.has(r.bus)) byBus.set(r.bus, []);
    byBus.get(r.bus)!.push(r);
  }

  const rows: LabelRow[] = [];
  const utility: StudyImport["utility"] = [];
  for (const [bus, rd] of byBus) {
    // Model Tag path: User Notes carries the Asset ID. The Asset ID in the name's parentheses must agree.
    let match: Match;
    const notes = rd.find((x) => x.userNotes)?.userNotes ?? "";
    if (notes) {
      const inName = /\(([^()]+)\)\s*$/.exec(bus)?.[1] ?? "";
      const id = notes.startsWith(`${input.site}-`) ? notes : `${input.site}-${notes}`;
      const short = id.slice(input.site.length + 1);
      if (inName && inName !== short && !short.startsWith(`${inName}.`)) match = { state: "Needs ID", assetId: id, note: `User Notes says ${id} but the SKM name says ${inName}. Check with KE.` };
      else if (reg.byId.has(id) || reg.byId.has(id.split(".")[0]!)) match = { state: "Matched", assetId: id, note: "" };
      else match = { state: "Needs ID", assetId: "", note: `User Notes ${id} is not in the site registry` };
    } else match = reg.matchBus(bus);

    const short = match.assetId.slice(input.site.length + 1);
    if (/^UTILITY\b/i.test(bus) || (match.state === "Matched" && reg.isUtility(short))) {
      utility.push({ bus, readings: rd });
      continue;
    }

    const od = rd.filter((x) => x.overduty);
    let kind: LabelKind;
    let winner: Reading;
    let interruptingKa: number | null = null;
    let failedDevice = "";
    let ratingNote = "";
    let isolateAt: Match;
    if (od.length) {
      kind = "OVERDUTY";
      // N21a: a protective device failed. Its rating comes from the device settings.
      const failed = rd.filter((x) => /N21a/.test(x.notes) && devices.get(x.device)?.interruptingKa != null).map((x) => x.device);
      failedDevice = failed[0] ?? "";
      if (failedDevice) {
        interruptingKa = devices.get(failedDevice)!.interruptingKa;
        ratingNote = `${failedDevice} is rated ${interruptingKa} kA, from the device settings`;
      } else if (rd.some((x) => /N21b/.test(x.notes))) {
        ratingNote = "SKM failed the bus equipment (note N21b), not a device. The rating is the enclosure SCCR, which is not in KE's files.";
      } else ratingNote = "KE's files do not say which rating failed.";
      // Isolate upstream of the failed device: the overduty reading cleared by a device that did not fail.
      const clear = od.filter((x) => !failed.includes(x.device) && !isLimitCase(x.device));
      winner = clear.length ? worstOf(clear) : worstOf(od);
      isolateAt = clear.length ? reg.matchDevice(winner.device) : failed.includes(winner.device)
        ? { state: "Needs ID", assetId: "", note: `SKM names only ${winner.device}, the overduty device. Isolate upstream of it.` }
        : reg.matchDevice(winner.device);
    } else {
      winner = worstOf(rd);
      kind = (winner.ieCal ?? 0) > 12 ? "ABOVE CEILING" : "STANDARD";
      isolateAt = reg.matchDevice(winner.device);
    }

    const record = match.assetId ? reg.byId.get(match.assetId) : undefined;
    const moveable = /moveable|movable/i.test(record?.conductorType ?? "");
    const lab70eIn = limitedApproachIn(winner.volts, moveable);
    const recordLab = boundaryInches(record?.limitedApproach);
    // The record's LAB prints. A blank record falls back to the 70E value and flags Check LAB.
    const labIn = recordLab ?? lab70eIn;
    const row: LabelRow = {
      bus,
      match,
      kind,
      stock: kind === "OVERDUTY" ? "DANGER" : "WARNING",
      volts: winner.volts,
      labIn,
      rabIn: restrictedApproachIn(winner.volts),
      lab70eIn,
      ieCal: kind === "OVERDUTY" ? null : winner.ieCal,
      workingDistanceIn: kind === "OVERDUTY" ? null : winner.workingDistanceIn,
      afbIn: kind === "OVERDUTY" ? null : winner.afbIn,
      faultKa: Math.max(...rd.map((x) => x.busBoltedKa)),
      interruptingKa,
      failedDevice,
      ratingNote,
      isolateAt,
      winner,
      readings: rd,
      buckets: match.state === "Matched" ? reg.bucketsOf(short) : [],
      status: [],
      warnings: [],
      summaryValue: null,
      summaryMatches: null,
      summaryDevice: "",
    };
    // No record yet (unmatched row): nothing to check LAB or voltage against until the Asset ID is set.
    row.status = statusOf({ ...row, labIn: record ? recordLab : lab70eIn }, record?.nominalVolts);
    if (kind === "OVERDUTY" && interruptingKa !== null && interruptingKa >= row.faultKa)
      row.warnings.push(`The rating (${interruptingKa} kA) is not below the fault current here (${row.faultKa.toFixed(1)} kA). SKM failed ${failedDevice} upstream or in another scenario. Confirm with KE before printing.`);

    const srows = summary.filter((x) => x.bus === bus);
    if (srows.length) {
      const so = srows.filter((x) => x.overduty);
      row.summaryValue = so.length ? "OVERDUTY" : oneDecimal(Math.max(...srows.map((x) => x.ieCal ?? 0)));
      const ours = kind === "OVERDUTY" ? "OVERDUTY" : oneDecimal(row.ieCal ?? 0);
      row.summaryMatches = row.summaryValue === ours;
      if (kind !== "OVERDUTY") {
        const top = srows.filter((x) => !x.overduty && oneDecimal(x.ieCal ?? 0) === ours).map((x) => x.device);
        if (top.length && !top.includes(winner.device)) row.summaryDevice = top[0]!;
      }
    }
    rows.push(row);
  }
  rows.sort((a, b) => (a.match.assetId || `~${a.bus}`).localeCompare(b.match.assetId || `~${b.bus}`, "en", { numeric: true }));

  return {
    site: input.site,
    path: modelTag ? "Model Tag" : "Legacy",
    rows,
    utility,
    readingCount: readings.length,
    busCount: byBus.size,
    summaryCount: summary.length,
    deviceCount: devices.size,
    settingChanges: [...devices.values()].flatMap((d) => d.changes.map((c) => ({ device: d.name, ...c }))),
  };
}

// ---- Review, Publish and Publish Check (sections 6 and 7). Shared by the page and the server. ----

/** A label row as stored: sites/{site}/afStudies/{studyId}/rows/{key}. */
export interface StoredRow extends LabelRow {
  key: string;
  /** No Label: a splice or load nobody opens. The reading stays in the study record. Approved 2026-10-21. */
  noLabel: boolean;
  print: boolean;
  published: boolean;
  /** Every change in Review, stamped. */
  edits: { field: string; from: string; to: string; by: string; byName: string; at: string }[];
}

/** The row's document ID: the SKM bus name with anything Firestore dislikes turned to "_". */
export function rowKeyOf(bus: string): string {
  return bus.replace(/[^A-Za-z0-9.-]+/g, "_").replace(/^_+|_+$/g, "") || "bus";
}

export const isReady = (r: Pick<StoredRow, "noLabel" | "status">) => !r.noLabel && !isBlocking(r.status);

export function storedRowsOf(s: StudyImport): StoredRow[] {
  const seen = new Set<string>();
  return s.rows.map((r) => {
    let key = rowKeyOf(r.bus);
    for (let n = 2; seen.has(key); n++) key = `${rowKeyOf(r.bus)}-${n}`;
    seen.add(key);
    return { ...r, key, noLabel: false, print: !isBlocking(r.status), published: false, edits: [] };
  });
}

/** Asset ID, buckets and status again, from the registry. Run after any change in Review. */
export function refreshRow(row: StoredRow, reg: StudyRegistry): StoredRow {
  const record = row.match.state === "Matched" ? reg.byId.get(row.match.assetId) : undefined;
  const moveable = /moveable|movable/i.test(record?.conductorType ?? "");
  const lab70eIn = limitedApproachIn(row.volts, moveable);
  const recordLab = boundaryInches(record?.limitedApproach);
  const short = row.match.assetId.slice(reg.site.length + 1);
  const out: StoredRow = {
    ...row,
    lab70eIn,
    labIn: recordLab ?? lab70eIn,
    buckets: row.match.state === "Matched" ? reg.bucketsOf(short) : [],
  };
  out.status = statusOf({ ...out, labIn: record ? recordLab : lab70eIn }, record?.nominalVolts);
  return out;
}

/** What Review may change. The study's numbers never. */
export type RowChange =
  | { field: "assetId"; value: string }
  | { field: "noLabel"; value: boolean }
  | { field: "isolateAt"; value: string }
  | { field: "interruptingKa"; value: number }
  | { field: "print"; value: boolean };

/** "ER1-MCC-01" or "10-ER1-MCC-01" to the full ID, upper case. */
export function fullIdOf(site: string, v: string): string {
  const s = v.trim().toUpperCase();
  return s.startsWith(`${site}-`) ? s : `${site}-${s}`;
}

/** Applies one Review change, or says why it cannot. The caller stamps who and when. */
export function applyRowChange(row: StoredRow, c: RowChange, reg: StudyRegistry): { row: StoredRow; from: string; to: string } | { problem: string } {
  if (row.published && c.field !== "print") return { problem: "This row is published. Import the study again to change it." };
  let next: StoredRow = { ...row };
  let from = "";
  let to = "";
  switch (c.field) {
    case "assetId": {
      const id = fullIdOf(reg.site, c.value);
      if (!reg.byId.has(id)) return { problem: `${id} is not in the site's assets.` };
      if (reg.isUtility(id.slice(reg.site.length + 1))) return { problem: `${id} is utility gear. It gets no label.` };
      from = row.noLabel ? "No Label" : row.match.assetId;
      to = id;
      next = { ...next, noLabel: false, match: { state: "Matched", assetId: id, note: "Set in Review" } };
      break;
    }
    case "noLabel":
      from = row.noLabel ? "No Label" : row.match.assetId || "Needs ID";
      to = c.value ? "No Label" : row.match.assetId || "Needs ID";
      next = { ...next, noLabel: c.value, print: c.value ? false : next.print };
      break;
    case "isolateAt": {
      const id = fullIdOf(reg.site, c.value);
      const base = id.split(".")[0]!;
      if (!reg.byId.has(base)) return { problem: `${base} is not in the site's assets.` };
      if (id.includes(".") && !/^[A-Z0-9-]+\.[A-Z0-9]{1,6}$/.test(id)) return { problem: "A part goes after one dot: 10-ER1-MSD-01.FUS." };
      from = row.isolateAt.assetId;
      to = id;
      next = { ...next, isolateAt: { state: "Matched", assetId: id, note: "Set in Review" } };
      break;
    }
    case "interruptingKa":
      if (row.kind !== "OVERDUTY") return { problem: "Only an overduty row carries an interrupting rating." };
      if (row.failedDevice) return { problem: "This rating comes from KE's device settings. A wrong number goes back to KE." };
      if (!(c.value > 0 && c.value < 1000)) return { problem: "Enter the rating in kA." };
      from = row.interruptingKa === null ? "" : String(row.interruptingKa);
      to = String(c.value);
      next = { ...next, interruptingKa: c.value, ratingNote: "Entered in Review" };
      break;
    case "print":
      if (c.value && !isReady(row)) return { problem: "Only a Ready row prints." };
      from = row.print ? "On" : "Off";
      to = c.value ? "On" : "Off";
      next = { ...next, print: c.value };
      break;
  }
  next = refreshRow(next, reg);
  if (next.kind === "OVERDUTY" && next.interruptingKa !== null && next.interruptingKa >= next.faultKa && !next.warnings.length)
    next.warnings = [`The rating (${next.interruptingKa} kA) is not below the fault current here (${next.faultKa.toFixed(1)} kA). Confirm before printing.`];
  // A row that just became Ready prints by default. Print stays as Robert left it otherwise.
  if (c.field !== "print" && !isReady(row) && isReady(next)) next.print = true;
  if (!isReady(next)) next.print = false;
  return { row: next, from, to };
}

/** Study info from the import form and the report cover. */
export interface StudyInfo {
  firm: string;
  ieaBy: string;
  jobNumber: string;
  /** MM/DD/YYYY, as the label prints it. */
  assessmentDate: string;
  engineer: string;
  method: string;
}

/** The AF Study line on the QR page: "KE · 06/10/2026". */
export const afStudyLineOf = (i: Pick<StudyInfo, "firm" | "assessmentDate">) => `${i.firm} · ${i.assessmentDate}`;
export const OVERDUTY_IE_STATUS = "Overduty. Energized work prohibited.";

/**
 * The study fields Publish writes on an asset (section 7). These are the fields the QR page and the JSP
 * already read. Nothing else on the asset is touched. Null means remove the field.
 */
export interface PublishFields {
  incidentEnergyCal: number | null;
  ieStatus: string | null;
  arcFlashBoundary: string | null;
  isOverduty: boolean;
  afStudy: string;
  afStudyId: string;
}

export function publishFieldsOf(row: Pick<StoredRow, "kind" | "ieCal" | "afbIn">, info: StudyInfo, studyId: string): PublishFields {
  const od = row.kind === "OVERDUTY";
  return {
    incidentEnergyCal: od || row.ieCal === null ? null : oneDecimal(row.ieCal),
    ieStatus: od ? OVERDUTY_IE_STATUS : null,
    arcFlashBoundary: od || row.afbIn === null ? null : `${row.afbIn} in`,
    isOverduty: od,
    afStudy: afStudyLineOf(info),
    afStudyId: studyId,
  };
}

/** The fields Publish writes, by Asset ID: each published row's asset and its buckets. */
export function publishPlanOf(rows: readonly StoredRow[], info: StudyInfo, studyId: string, which: "to publish" | "published" = "to publish"): Map<string, PublishFields> {
  const plan = new Map<string, PublishFields>();
  for (const r of rows) {
    if (which === "published" ? !r.published : !isReady(r) || !r.print || r.published) continue;
    const f = publishFieldsOf(r, info, studyId);
    plan.set(r.match.assetId, f);
    for (const b of r.buckets) if (!plan.has(b)) plan.set(b, f);
  }
  return plan;
}

/** One label row, named exactly like the Label_Data headers of AF_Label_Data.xlsx (the standard). */
export function labelDataOf(row: StoredRow, site: string, info: StudyInfo, studyId: string): Record<string, string | number | null> {
  const od = row.kind === "OVERDUTY";
  return {
    Print: row.print ? "Y" : "N",
    Site_ID: site,
    Equipment_ID: row.match.assetId,
    Equipment_Description: "",
    Study_Bus_Name: row.bus,
    Nominal_Voltage_V: row.volts,
    Voltage_Type: "AC",
    LAB_in: row.labIn,
    RAB_in: row.rabIn,
    Incident_Energy_cal: od ? null : row.ieCal,
    Working_Distance_in: od ? null : row.workingDistanceIn,
    Arc_Flash_Boundary_in: od ? null : row.afbIn,
    Available_Fault_Current_kA: row.faultKa,
    Interrupting_Rating_kA: od ? row.interruptingKa : null,
    Isolate_At: row.isolateAt.assetId,
    Assessment_Date: info.assessmentDate,
    IEA_By: info.ieaBy,
    Notes: `${info.firm} job ${info.jobNumber}. Cleared by ${row.winner.device}${row.winner.incoming ? " (Incoming)" : ""}, ${row.winner.tripSeconds.toFixed(3)} s.`,
    Label_Type: row.kind,
    studyId,
  };
}

export interface PublishMiss {
  assetId: string;
  field: keyof PublishFields | "asset";
  found: string;
  expected: string;
}

const shown = (v: unknown) => (v === null || v === undefined || v === "" ? "blank" : typeof v === "boolean" ? (v ? "Yes" : "No") : String(v));

/** Publish Check (Robert, I8): read every published asset back and name each field that does not match. */
export function publishCheckOf(plan: ReadonlyMap<string, PublishFields>, found: ReadonlyMap<string, Record<string, unknown> | null>): PublishMiss[] {
  const misses: PublishMiss[] = [];
  for (const [assetId, want] of plan) {
    const got = found.get(assetId);
    if (!got) {
      misses.push({ assetId, field: "asset", found: "missing", expected: "the asset" });
      continue;
    }
    for (const k of Object.keys(want) as (keyof PublishFields)[]) {
      const a = got[k] ?? null;
      const e = want[k];
      if (a !== e) misses.push({ assetId, field: k, found: shown(a), expected: shown(e) });
    }
  }
  return misses;
}

const FIELD_WORDS: Record<PublishMiss["field"], string> = {
  incidentEnergyCal: "Incident Energy",
  ieStatus: "IE status",
  arcFlashBoundary: "Arc Flash Boundary",
  isOverduty: "Overduty",
  afStudy: "AF Study",
  afStudyId: "Study link",
  asset: "Asset",
};

/** The report line for one miss: "10-EA2-PNL-02: Overduty reads No, should be Yes." */
export const missWords = (m: PublishMiss) =>
  m.field === "asset" ? `${m.assetId}: the asset is missing.` : `${m.assetId}: ${FIELD_WORDS[m.field]} reads ${m.found}, should be ${m.expected}.`;

/** The counts at the top of Review. */
export function countsOf(rows: readonly StoredRow[]) {
  const live = rows.filter((r) => !r.noLabel);
  return {
    labels: live.length,
    ready: live.filter(isReady).length,
    needSomething: live.filter((r) => isBlocking(r.status)).length,
    warning: live.filter((r) => r.stock === "WARNING").length,
    danger: live.filter((r) => r.stock === "DANGER").length,
    noLabel: rows.length - live.length,
    published: rows.filter((r) => r.published).length,
    summaryMatches: rows.filter((r) => r.summaryMatches === true).length,
  };
}

/** MM/DD/YYYY or YYYY-MM-DD to MM/DD/YYYY. Anything else is null. */
export function usDateOf(v: string): string | null {
  const s = v.trim();
  let m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) return `${m[1]!.padStart(2, "0")}/${m[2]!.padStart(2, "0")}/${m[3]}`;
  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : null;
}

/** The import form, cleaned, or what is wrong with it. */
export function studyInfoOf(raw: Partial<Record<keyof StudyInfo, unknown>>): StudyInfo | { problem: string } {
  const t = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  const info: StudyInfo = { firm: t(raw.firm), ieaBy: t(raw.ieaBy), jobNumber: t(raw.jobNumber), assessmentDate: t(raw.assessmentDate), engineer: t(raw.engineer), method: t(raw.method) || "IEEE 1584-2018" };
  if (!info.firm) return { problem: "Enter the firm that ran the study." };
  if (!info.ieaBy) return { problem: "Enter IEA By. It prints on every label." };
  const d = usDateOf(info.assessmentDate);
  if (!d) return { problem: "Enter the assessment date as MM/DD/YYYY. It prints on every label." };
  info.assessmentDate = d;
  for (const [k, v] of Object.entries(info)) if (v.length > 80) return { problem: `${k} is too long.` };
  return info;
}

// ---- Which of KE's files is which (Import, section 3) ----

export interface ReadFile {
  name: string;
  /** Each tab as rows of cells. Empty for a PDF. */
  sheets: { name: string; rows: SheetRows }[];
}

export type FileRole = "detail" | "summary" | "settings" | "report" | "not used";

export interface FileSort {
  name: string;
  role: FileRole;
  /** The tab the role came from. */
  sheet: string;
  /** "109 readings on 60 buses", "55 devices", "For the archive". */
  line: string;
}

function isArcFlashTab(rows: SheetRows): boolean {
  try {
    columnsOf(rows);
    return true;
  } catch {
    return false;
  }
}

/** Sorts KE's files. The Detail is the one named Detail, or else the arc flash sheet with the most readings. */
export function sortStudyFiles(files: readonly ReadFile[]): FileSort[] {
  const out: (FileSort & { readings: number })[] = files.map((f) => {
    if (/\.pdf$/i.test(f.name)) return { name: f.name, role: "report", sheet: "", line: "For the archive", readings: 0 };
    for (const s of f.sheets) {
      const head = (s.rows[0] ?? []).map(cell);
      if (head[0] === "Component" && head[1] === "Field") {
        const n = s.rows.filter((r) => cell(r[0])).length - 1;
        return { name: f.name, role: "settings", sheet: s.name, line: `${n} devices`, readings: 0 };
      }
      if (isArcFlashTab(s.rows)) {
        const rd = readArcFlashSheet(s.rows);
        return { name: f.name, role: "summary", sheet: s.name, line: `${rd.length} readings on ${new Set(rd.map((x) => x.bus)).size} buses`, readings: rd.length };
      }
    }
    return { name: f.name, role: "not used", sheet: "", line: "No SKM arc flash or device settings tab in this file", readings: 0 };
  });
  const af = out.filter((o) => o.role === "summary");
  const named = af.find((o) => /detail/i.test(o.name));
  const detail = named ?? [...af].sort((a, b) => b.readings - a.readings)[0];
  if (detail) detail.role = "detail";
  // Only one of each is used. Extras are listed as not used.
  const seen = new Set<FileRole>();
  for (const o of out) {
    if (o.role === "not used" || o.role === "report") continue;
    if (seen.has(o.role)) {
      o.role = "not used";
      o.line = "Another file already fills this place";
    }
    seen.add(o.role);
  }
  return out.map(({ readings: _r, ...o }) => o);
}

// ---- Create Labels (section 8) ----

/** The rows Create Labels draws: published, Print on. Each as a Label_Data record, in Asset ID order. */
export function labelRecordsOf(rows: readonly StoredRow[], site: string, info: StudyInfo, studyId: string): { row: number; rec: Record<string, unknown> }[] {
  return rows
    .filter((r) => r.published && r.print && !r.noLabel)
    .sort((a, b) => a.match.assetId.localeCompare(b.match.assetId, "en", { numeric: true }))
    .map((r, i) => ({ row: i + 2, rec: labelDataOf(r, site, info, studyId) }));
}

/** A label set's ID: L-20261021T1700. One per press of Create Labels. */
export const labelSetIdOf = (atIso: string) => `L-${atIso.replace(/[-:]/g, "").slice(0, 13)}${atIso.slice(17, 19)}`;

/** The four files of a set, as af_label.py names them. */
export const LABEL_FILES = ["AF_Labels_WARNING_Print.pdf", "AF_Labels_DANGER_Print.pdf", "AF_Labels_Preview.pdf", "AF_Label_Exceptions.csv"] as const;
